<div class="relative">
  <!-- Loading overlay -->
  <div class="transition-all duration-200" [class.blur-sm]="isLoadingData()"
    [class.opacity-50]="isLoadingData()" [class.pointer-events-none]="isLoadingData()"
    [attr.aria-busy]="isLoadingData()">
    <form [formGroup]="serviceForm" class="space-y-6">

      <!-- Section: Información básica -->
      <fieldset>
        <legend class="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Información básica
        </legend>
        <div class="space-y-4">
          <!-- Name -->
          <div>
            <label for="name" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              Nombre del servicio <span class="text-error-500" aria-hidden="true">*</span>
            </label>
            <input type="text" id="name" formControlName="name"
              placeholder="Ej: Corte de cabello, Manicure gel, Masaje relajante"
              aria-required="true"
              [attr.aria-invalid]="serviceForm.get('name')?.invalid && serviceForm.get('name')?.touched"
              class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30" />
            @if (serviceForm.get('name')?.invalid && serviceForm.get('name')?.touched) {
              <p class="mt-1 text-xs text-error-500" role="alert">
                @if (serviceForm.get('name')?.hasError('required')) {
                  El nombre es obligatorio
                } @else if (serviceForm.get('name')?.hasError('minlength')) {
                  Mínimo 3 caracteres
                }
              </p>
            }
          </div>

          <!-- Description -->
          <div>
            <label for="description" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              Descripción
              <span class="ml-1 text-xs font-normal text-gray-400">opcional</span>
            </label>
            <textarea id="description" formControlName="description" rows="2"
              placeholder="Describe brevemente qué incluye el servicio"
              class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 w-full resize-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"></textarea>
          </div>
        </div>
      </fieldset>

      <hr class="border-gray-100 dark:border-gray-800" />

      <!-- Section: Precio y duración -->
      <fieldset>
        <legend class="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Precio y duración
        </legend>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <!-- Price -->
          <div>
            <label for="price" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              Precio <span class="text-error-500" aria-hidden="true">*</span>
            </label>
            <div class="relative">
              <span class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
              <input type="text" inputmode="numeric" id="price" formControlName="price"
                placeholder="50.000" autocomplete="off"
                aria-required="true"
                (input)="onPriceInput($event)"
                class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pl-8 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30" />
            </div>
            @if (serviceForm.get('price')?.invalid && serviceForm.get('price')?.touched) {
              <p class="mt-1 text-xs text-error-500" role="alert">El precio es obligatorio</p>
            }
          </div>

          <!-- Duration -->
          <div>
            <label for="durationMinutes" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              Duración <span class="text-error-500" aria-hidden="true">*</span>
            </label>
            <div class="relative">
              <select formControlName="durationMinutes" id="durationMinutes"
                aria-required="true"
                class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent bg-none px-4 py-2.5 pr-10 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
                <option value="" class="text-gray-400">Selecciona duración</option>
                @for (duration of durationOptions; track duration.value) {
                  <option [value]="duration.value" class="text-gray-700 dark:bg-gray-900 dark:text-gray-400">
                    {{ duration.label }}
                  </option>
                }
              </select>
              <svg class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            @if (serviceForm.get('durationMinutes')?.invalid && serviceForm.get('durationMinutes')?.touched) {
              <p class="mt-1 text-xs text-error-500" role="alert">Selecciona la duración</p>
            }
          </div>
        </div>
      </fieldset>

      <hr class="border-gray-100 dark:border-gray-800" />

      <!-- Section: Imagen -->
      <fieldset>
        <legend class="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Imagen
          <span class="text-xs font-normal text-gray-400">opcional</span>
        </legend>
        <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Se mostrará en el micrositio cuando los clientes seleccionen el servicio
        </p>
        <div class="flex items-center gap-4">
          <!-- Preview thumbnail -->
          <div class="flex size-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
            @if (serviceImagePreview()) {
              <img [src]="serviceImagePreview()!" alt="Vista previa" class="h-full w-full object-cover" />
            } @else {
              <svg class="size-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          </div>
          <!-- Upload controls -->
          <div class="flex flex-col gap-1.5">
            <label
              class="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-100 dark:bg-brand-500/10 dark:text-brand-200"
              aria-label="Seleccionar imagen del servicio">
              <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {{ serviceImagePreview() ? 'Cambiar' : 'Subir imagen' }}
              <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" class="hidden" (change)="onServiceImageSelected($event)" />
            </label>
            @if (serviceImagePreview()) {
              <button type="button" (click)="clearServiceImage()"
                class="w-fit text-xs text-gray-400 transition-colors hover:text-error-500">
                Eliminar
              </button>
            }
            <p class="text-[10px] text-gray-400">JPEG, PNG o WebP. Máx 5 MB</p>
          </div>
        </div>
      </fieldset>

      <hr class="border-gray-100 dark:border-gray-800" />

      <!-- Section: Paquete de sesiones -->
      <fieldset>
        <legend class="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Paquete de sesiones
        </legend>
        <label for="hasSessions"
          class="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
          [class.border-brand-300]="serviceForm.get('hasSessions')?.value"
          [class.bg-brand-50]="serviceForm.get('hasSessions')?.value"
          [class.dark:border-brand-700]="serviceForm.get('hasSessions')?.value"
          [class.dark:bg-brand-900/20]="serviceForm.get('hasSessions')?.value">
          <input type="checkbox" formControlName="hasSessions" id="hasSessions"
            class="size-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Este servicio se vende en múltiples sesiones
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500">
              Ideal para tratamientos que requieren varias citas
            </p>
          </div>
        </label>

        @if (serviceForm.get('hasSessions')?.value) {
          <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label for="sessionsCount" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                Cantidad de sesiones <span class="text-error-500" aria-hidden="true">*</span>
              </label>
              <input type="number" min="2" id="sessionsCount" formControlName="sessionsCount"
                placeholder="Ej: 5"
                aria-required="true"
                class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30" />
              @if (serviceForm.get('sessionsCount')?.invalid && serviceForm.get('sessionsCount')?.touched) {
                <p class="mt-1 text-xs text-error-500" role="alert">Mínimo 2 sesiones</p>
              }
            </div>
            <div>
              <label for="sessionIntervalDays" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                Días entre sesiones <span class="text-error-500" aria-hidden="true">*</span>
              </label>
              <input type="number" min="1" id="sessionIntervalDays" formControlName="sessionIntervalDays"
                placeholder="Ej: 7"
                aria-required="true"
                class="dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30" />
              @if (serviceForm.get('sessionIntervalDays')?.invalid && serviceForm.get('sessionIntervalDays')?.touched) {
                <p class="mt-1 text-xs text-error-500" role="alert">Mínimo 1 día</p>
              }
            </div>
          </div>
        }
      </fieldset>

      <hr class="border-gray-100 dark:border-gray-800" />

      <!-- Section: Profesionales asignados -->
      <fieldset>
        <legend class="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Profesionales asignados
          @if (selectedCount > 0) {
            <span class="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
              {{ selectedCount }} seleccionado{{ selectedCount !== 1 ? 's' : '' }}
            </span>
          } @else {
            <span class="text-xs font-normal text-gray-400">opcional</span>
          }
        </legend>
        <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Selecciona quiénes pueden realizar este servicio
        </p>

        <!-- Selected professionals tags -->
        @if (selectedCount > 0) {
          <div class="mb-3 flex flex-wrap gap-1.5">
            @for (profId of assignedProfessionalIds(); track profId) {
              @for (prof of professionals(); track prof.userId) {
                @if (prof.userId === profId) {
                  <span class="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 py-0.5 pl-1 pr-2.5 text-xs font-medium text-brand-700 dark:border-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                    <span class="flex size-5 items-center justify-center rounded-full text-[9px] font-semibold text-white"
                      [style.background-color]="prof.color || '#6366f1'">
                      {{ getInitial(prof) }}
                    </span>
                    {{ getProfessionalDisplayName(prof) }}
                    <button type="button" (click)="toggleProfessional(prof.userId)"
                      class="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-brand-200 dark:hover:bg-brand-500/30"
                      [attr.aria-label]="'Quitar a ' + getProfessionalDisplayName(prof)">
                      <svg class="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </span>
                }
              }
            }
          </div>
        }

        <div class="rounded-lg border border-gray-200 dark:border-gray-700">
          @if (professionals().length === 0) {
            <div class="flex flex-col items-center gap-1 px-4 py-6 text-center">
              <svg class="size-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p class="text-sm text-gray-500 dark:text-gray-400">No hay profesionales</p>
              <p class="text-xs text-gray-400">Crea profesionales en Configuración</p>
            </div>
          } @else {
            <!-- Professional search -->
            @if (professionals().length > 5) {
              <div class="border-b border-gray-100 p-2 dark:border-gray-700">
                <div class="relative">
                  <svg class="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <input type="text" [value]="professionalSearchTerm()"
                    (input)="onProfessionalSearch($any($event.target).value)"
                    placeholder="Buscar profesional..."
                    aria-label="Buscar profesional"
                    class="h-8 w-full rounded-md border border-gray-200 bg-gray-50 pl-8 pr-3 text-xs text-gray-700 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:placeholder:text-gray-500" />
                </div>
              </div>
            }
            <!-- Professional list -->
            <div class="max-h-56 overflow-y-auto">
              @if (filteredProfessionals().length === 0) {
                <div class="px-4 py-4 text-center text-xs text-gray-400">
                  No se encontraron profesionales
                </div>
              }
              @for (professional of filteredProfessionals(); track professional.userId) {
                <label
                  class="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2.5 transition-all duration-150 last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                  [class.bg-brand-50]="isProfessionalSelected(professional.userId)"
                  [class.dark:bg-brand-900/20]="isProfessionalSelected(professional.userId)">
                  <input type="checkbox"
                    [checked]="isProfessionalSelected(professional.userId)"
                    (change)="toggleProfessional(professional.userId)"
                    [attr.aria-label]="'Asignar a ' + getProfessionalDisplayName(professional)"
                    class="size-4 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <!-- Avatar -->
                  @if (professional.avatarUrl) {
                    <img [src]="professional.avatarUrl" [alt]="getProfessionalDisplayName(professional)"
                      class="size-8 flex-shrink-0 rounded-full object-cover" />
                  } @else {
                    <span class="flex size-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                      [style.background-color]="professional.color || '#6366f1'">
                      {{ getInitial(professional) }}
                    </span>
                  }
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-gray-800 dark:text-white/90">
                      {{ getProfessionalDisplayName(professional) }}
                    </p>
                    @if (professional.email) {
                      <p class="truncate text-xs text-gray-400">{{ professional.email }}</p>
                    }
                  </div>
                  @if (isProfessionalSelected(professional.userId)) {
                    <svg class="size-4 flex-shrink-0 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  }
                </label>
              }
            </div>
          }
        </div>
      </fieldset>

    </form>
  </div>
</div>
